import React, { useCallback, useMemo } from 'react'
import cx from 'classnames'
import { l } from '@manychat/manyui'

import { FieldType } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import {
  FilterCondition,
  FilterConditionOperator,
  FilterFieldOperatorData,
} from 'common/filter/interfaces'
import { FieldTypesOperators, UNARY_OPERATORS } from 'common/filter/models/AudienceFilter/constants'
import { getDefaultConditionData } from 'common/filter/models/AudienceFilter/helpers'
import { PopoverList } from 'components/MuiPopover'

import { ConditionValue } from './components/ConditionValue'

import cm from './ConditionEditor.module.css'

interface ConditionEditorProps {
  condition?: FilterCondition
  operatorDesc: FilterFieldOperatorData
  fieldDesc: Field
  isBuilder?: boolean
  allowDateFormula?: boolean
  useChangedLabelForOperators?: boolean
  onChange: (data: Partial<FilterCondition>, closePopover?: boolean) => void
  onRequestClose: () => void
}

export const ConditionEditor = ({
  condition,
  operatorDesc,
  fieldDesc,
  isBuilder = false,
  allowDateFormula = false,
  useChangedLabelForOperators = false,
  onChange,
  onRequestClose,
}: ConditionEditorProps) => {
  const handleOperatorChange = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const operator = event.currentTarget.dataset.op as FilterConditionOperator

      if (!operator) return

      const closePopover = UNARY_OPERATORS.includes(operator)
      const baseData = { operator }
      const extraData =
        fieldDesc.type === FieldType.SYSTEM_CURRENT_DATETIME
          ? getDefaultConditionData(fieldDesc.type, operator)
          : {}

      onChange({ ...baseData, ...extraData }, closePopover)
    },
    [fieldDesc.type, onChange],
  )

  const handleValueChange = useCallback(
    (filter: Partial<FilterCondition>, closePopover?: boolean) => {
      onChange(filter, closePopover)
    },
    [onChange],
  )

  const handleValueKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && condition?.value != null && condition?.value !== '') {
        onRequestClose()
      }
    },
    [condition?.value, onRequestClose],
  )

  const operators = useMemo(() => {
    const baseOperators = FieldTypesOperators[fieldDesc.type]

    // Special case: gender field excludes the second operator
    if (fieldDesc.id === 'gender') {
      return baseOperators?.filter((_, index) => index !== 1)
    }

    return baseOperators
  }, [fieldDesc.type, fieldDesc.id])

  return (
    <div className={cm.conditionEditor}>
      <PopoverList>
        {operators?.map((op) => {
          const changedLabel = op.translatedChangedLabel
            ? l.getString(op.translatedChangedLabel)
            : op.changedLabel
          const label = op.translatedLabel ? l.getString(op.translatedLabel) : op.label

          return (
            <a key={op.value} onClick={handleOperatorChange} data-op={op.value}>
              <span
                className={cx(cm.popoverEl, {
                  [cm.popoverActiveEL]: operatorDesc.value === op.value,
                })}
              >
                {useChangedLabelForOperators ? changedLabel : label}
              </span>
            </a>
          )
        })}
      </PopoverList>
      <div
        className={cm.conditionValue}
        onKeyDown={handleValueKeyDown}
        data-test-id="field-condition-editor"
        data-onboarding-id="field-condition-editor"
      >
        <ConditionValue
          fieldDesc={fieldDesc}
          operatorDesc={operatorDesc}
          condition={condition as FilterCondition}
          onChange={handleValueChange}
          allowDateFormula={allowDateFormula}
          isBuilder={isBuilder}
        />
      </div>
    </div>
  )
}
